import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Notifications } from "../utils/api";

function fmt(s) {
  if (!s) return "";
  return String(s).replace("T", " ").replace(".000Z", "");
}

function safeJsonParse(s) {
  if (!s || typeof s !== "string") return null;
  const t = s.trim();
  if (!t) return null;
  // chỉ thử parse khi có vẻ là JSON
  if (!(t.startsWith("{") || t.startsWith("["))) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

function pick(obj, keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
}

function buildDisplay(formType, body) {
  const payload = safeJsonParse(body);
  const type = String(formType || "").trim();

  if (!payload) {
    return {
      previewLines: body ? [String(body).slice(0, 180)] : [],
      fields: [],
      raw: body || "",
    };
  }

  if (type === "supplier") {
    const supplierName = String(pick(payload, ["supplierName", "company", "companyName", "supplier_name"]) || "").trim();
    const contactName = String(pick(payload, ["contactName", "contact", "contact_name"]) || "").trim();
    const phone = String(pick(payload, ["phone", "contactPhone", "tel"]) || "").trim();
    const address = String(pick(payload, ["address"]) || "").trim();
    const product = String(pick(payload, ["product", "products"]) || "").trim();

    const fields = [
      { label: "Tên nhà cung cấp", value: supplierName },
      { label: "Người liên hệ", value: contactName },
      { label: "Điện thoại", value: phone },
      { label: "Địa chỉ", value: address },
      { label: "Sản phẩm muốn cung cấp", value: product },
    ].filter((x) => x.value);

    const previewLines = [
      supplierName && `Nhà cung cấp: ${supplierName}`,
      contactName && `Liên hệ: ${contactName}`,
      phone && `SĐT: ${phone}`,
    ].filter(Boolean).slice(0, 3);

    return { previewLines, fields, raw: JSON.stringify(payload, null, 2) };
  }

  if (type === "love") {
    const fullName = String(pick(payload, ["fullName", "full_name", "name"]) || "").trim();
    const gender = String(pick(payload, ["gender"]) || "").trim();
    const birthYear = String(pick(payload, ["birthYear", "yearOfBirth"]) || "").trim();
    const maritalStatus = String(pick(payload, ["maritalStatus", "marital"]) || "").trim();
    const height = String(pick(payload, ["height"]) || "").trim();
    const weight = String(pick(payload, ["weight"]) || "").trim();
    const phone = String(pick(payload, ["phone"]) || "").trim();
    const education = String(pick(payload, ["education"]) || "").trim();
    const province = String(pick(payload, ["province"]) || "").trim();
    const specialty = String(pick(payload, ["specialty", "job"]) || "").trim();
    const currentJob = String(pick(payload, ["currentJob"]) || "").trim();
    const partnerRequest = String(pick(payload, ["partnerRequest", "partnerNeed"]) || "").trim();

    const fields = [
      { label: "Họ và tên", value: fullName },
      { label: "Giới tính", value: gender },
      { label: "Năm sinh", value: birthYear },
      { label: "Tình trạng hôn nhân", value: maritalStatus },
      { label: "Chiều cao", value: height },
      { label: "Cân nặng", value: weight },
      { label: "Số điện thoại", value: phone },
      { label: "Học vấn", value: education },
      { label: "Hiện ở tỉnh", value: province },
      { label: "Nghề chuyên môn", value: specialty },
      { label: "Công việc hiện tại", value: currentJob },
      { label: "Yêu cầu người muốn kết đôi", value: partnerRequest, wide: true },
    ].filter((x) => x.value);

    const previewLines = [
      fullName && `Họ tên: ${fullName}`,
      phone && `SĐT: ${phone}`,
      province && `Tỉnh: ${province}`,
    ].filter(Boolean).slice(0, 3);

    return { previewLines, fields, raw: JSON.stringify(payload, null, 2) };
  }

  if (type === "feedback") {
    const message = String(pick(payload, ["message", "content", "text"]) || "").trim();
    const phone = String(pick(payload, ["phone"]) || "").trim();

    const fields = [
      { label: "Số điện thoại", value: phone },
      { label: "Nội dung góp ý/phản ánh", value: message, wide: true },
    ].filter((x) => x.value);

    const previewLines = [
      phone && `SĐT: ${phone}`,
      message && `Nội dung: ${message}`,
    ].filter(Boolean);

    // cắt gọn preview
    const compact = [];
    for (const line of previewLines) {
      compact.push(line.length > 120 ? line.slice(0, 120) + "…" : line);
      if (compact.length >= 2) break;
    }

    return { previewLines: compact, fields, raw: JSON.stringify(payload, null, 2) };
  }

  // Fallback: render key-values cơ bản
  const entries = Object.entries(payload || {}).slice(0, 12);
  const fields = entries.map(([k, v]) => ({ label: k, value: String(v) }));
  const previewLines = fields.slice(0, 3).map((x) => `${x.label}: ${x.value}`);

  return { previewLines, fields, raw: JSON.stringify(payload, null, 2) };
}

export default function NotificationsPage() {
  const [loading, setLoading] = useState(true);
  const [onlyUnread, setOnlyUnread] = useState(true);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  async function load() {
    try {
      setLoading(true);
      const data = await Notifications.list({ unread: onlyUnread ? 1 : 0 });
      setItems(Array.isArray(data) ? data : data?.items || data?.rows || []);
    } catch (e) {
      toast.error(e?.message || "Không tải được thông báo");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyUnread]);

  const badge = useMemo(() => {
    const unread = items.filter((x) => !x.is_read).length;
    return { unread, total: items.length };
  }, [items]);

  async function markRead(id) {
    try {
      await Notifications.markRead(id);
      await load();
    } catch (e) {
      toast.error(e?.message || "Không cập nhật được");
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-lg font-semibold">Thông báo</div>
          <div className="text-sm text-gray-500">Chưa đọc: {badge.unread} / Tổng: {badge.total}</div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyUnread}
            onChange={(e) => setOnlyUnread(e.target.checked)}
          />
          Chỉ chưa đọc
        </label>
      </div>

      {loading ? (
        <div className="p-4 text-gray-500">Đang tải…</div>
      ) : items.length === 0 ? (
        <div className="p-4 text-gray-500">Chưa có thông báo.</div>
      ) : (
        <div className="space-y-3">
          {items.map((n) => {
            const view = buildDisplay(n.form_type, n.body);
            return (
              <div
                key={n.id}
                className={`rounded-xl border p-4 bg-white shadow-sm ${n.is_read ? "opacity-80" : ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">
                      {!n.is_read && (
                        <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2" />
                      )}
                      {n.title}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {fmt(n.created_at)} · Loại: {n.form_type}
                    </div>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <button
                      className="px-3 py-1 rounded-lg border text-sm hover:bg-gray-50"
                      onClick={() => {
                        setOpen(n);
                      }}
                    >
                      Xem
                    </button>
                    {!n.is_read && (
                      <button
                        className="px-3 py-1 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
                        onClick={() => markRead(n.id)}
                      >
                        Đã đọc
                      </button>
                    )}
                  </div>
                </div>

                {/* Preview (không hiển thị JSON) */}
                <div className="mt-3 text-sm text-gray-700">
                  {view.previewLines?.length ? (
                    <ul className="space-y-1">
                      {view.previewLines.map((line, idx) => (
                        <li key={idx} className="flex gap-2">
                          <span className="text-gray-400">•</span>
                          <span className="line-clamp-1">{line}</span>
                        </li>
                      ))}
                    </ul>
                  ) : n.body ? (
                    <div className="line-clamp-2">{String(n.body)}</div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal detail (mobile-first bottom sheet) */}
      {open && (() => {
        const view = buildDisplay(open.form_type, open.body);

        return (
          <div
            className="fixed inset-0 bg-black/50 z-[60]"
            onMouseDown={() => setOpen(null)}
          >
            <div className="absolute inset-0 flex items-end sm:items-center justify-center p-0 sm:p-4">
              <div
                className="w-full sm:max-w-xl bg-white rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden
                     max-h-[92svh] sm:max-h-[calc(100svh-2rem)] flex flex-col"
                onMouseDown={(e) => e.stopPropagation()}
              >
                {/* Header (sticky) */}
                <div className="px-4 py-3 border-b bg-white sticky top-0 z-10">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-base sm:text-lg truncate">
                        {open.title}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 mt-0.5">
                        {fmt(open.created_at)} · Loại: {open.form_type}
                      </div>
                    </div>

                    <button
                      className="shrink-0 w-9 h-9 rounded-full hover:bg-gray-100 grid place-items-center"
                      onClick={() => setOpen(null)}
                      aria-label="Đóng"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Body (SCROLL) */}
                <div
                  className="px-4 py-4 flex-1 overflow-y-scroll overscroll-contain"
                  style={{ WebkitOverflowScrolling: "touch" }}
                >
                  {view?.fields?.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {view.fields.map((f, idx) => (
                        <div
                          key={idx}
                          className={`rounded-xl border p-3 bg-gray-50 ${f.wide ? "sm:col-span-2" : ""
                            }`}
                        >
                          <div className="text-xs text-gray-500 mb-1">{f.label}</div>
                          <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                            {f.value || "(trống)"}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap break-words text-sm text-gray-800">
                      {open.body || "(Không có nội dung)"}
                    </div>
                  )}
                </div>

                {/* Footer (sticky) */}
                <div className="px-4 py-3 border-t bg-white sticky bottom-0 z-10 flex justify-end gap-2">
                  <button className="px-4 py-2 rounded-lg border" onClick={() => setOpen(null)}>
                    Đóng
                  </button>

                  {!open.is_read && (
                    <button
                      className="px-4 py-2 rounded-lg bg-red-600 text-white"
                      onClick={async () => {
                        await markRead(open.id);
                        setOpen(null);
                      }}
                    >
                      Đã đọc
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
}
